import { Component, OnInit } from '@angular/core';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';
import { isReviewComponentType } from '../shared/component-form.helpers';

declare let $: any;

type ReviewAction = 'reject' | 'archive';

interface ReviewStatusOption {
  value: number | '';
  labelKey: string;
}

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss'],
})
export class ReviewsComponent implements OnInit {
  readonly REVIEW_STATUS_PENDING = 0;
  readonly REVIEW_STATUS_APPROVED = 1;
  readonly REVIEW_STATUS_REJECTED = 2;
  readonly REVIEW_STATUS_ARCHIVED = 3;

  readonly statusOptions: ReviewStatusOption[] = [
    { value: '', labelKey: 'ALL' },
    { value: this.REVIEW_STATUS_PENDING, labelKey: 'REVIEW_PENDING' },
    { value: this.REVIEW_STATUS_APPROVED, labelKey: 'REVIEW_APPROVED' },
    { value: this.REVIEW_STATUS_REJECTED, labelKey: 'REVIEW_REJECTED' },
    { value: this.REVIEW_STATUS_ARCHIVED, labelKey: 'REVIEW_ARCHIVED' },
  ];

  reviews: any[] = [];
  reviewComponents: any[] = [];
  selectedReview: any = null;
  pendingReview: any = null;
  pendingAction: ReviewAction | null = null;

  loading = false;
  actionLoading = false;
  search = '';
  status: number | '' = '';
  componentId: number | '' = '';
  counter = 0;
  amount = 25;
  totalAmount = 0;

  constructor(
    private lambdaService: LambdaService,
    public themingService: ThemingService
  ) {}

  ngOnInit(): void {
    this.getReviewComponents();
    this.getReviews();
  }

  async getReviews(resetCounter: boolean = false): Promise<void> {
    if (resetCounter) {
      this.counter = 0;
    }

    this.loading = true;
    try {
      const response: any = await this.lambdaService.getReviews(this.themingService.shopId, {
        status: this.status,
        componentId: this.componentId,
        search: this.search,
        counter: this.counter,
        amount: this.amount,
      });

      this.reviews = this.extractReviews(response);
      this.totalAmount = Number(response?.totalAmount || response?.total || response?.count || this.reviews.length || 0);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      this.reviews = [];
      $('#errorReviewModal').modal('show');
    } finally {
      this.loading = false;
    }
  }

  async getReviewComponents(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getComponents(this.themingService.shopId);
      const components = Array.isArray(response?.data) ? response.data : [];
      this.reviewComponents = components.filter((component: any) =>
        isReviewComponentType(Number(component.componentTypeId || 0), [], component.componentTypeName || '')
      );
    } catch (error) {
      console.error('Error fetching review components:', error);
      this.reviewComponents = [];
    }
  }

  async approveReview(review: any): Promise<void> {
    await this.updateReviewStatus(review, this.REVIEW_STATUS_APPROVED);
  }

  confirmReviewAction(review: any, action: ReviewAction): void {
    this.pendingReview = review;
    this.pendingAction = action;
  }

  async handleConfirmAction(confirmed: boolean): Promise<void> {
    if (!confirmed || !this.pendingReview || !this.pendingAction) {
      this.clearPendingAction();
      return;
    }

    const status = this.pendingAction === 'reject' ? this.REVIEW_STATUS_REJECTED : this.REVIEW_STATUS_ARCHIVED;
    await this.updateReviewStatus(this.pendingReview, status, this.pendingAction === 'archive');
    this.clearPendingAction();
  }

  async openReviewDetail(review: any): Promise<void> {
    this.selectedReview = review;
    try {
      const response: any = await this.lambdaService.getReviewById(Number(review.reviewId || 0), this.themingService.shopId);
      this.selectedReview = response?.review || response?.data || response || review;
    } catch (error) {
      console.error('Error fetching review detail:', error);
    }

    $('#reviewDetailModal').modal('show');
  }

  closeModal(): void {
    $('#reviewDetailModal').modal('hide');
    $('#successReviewModal').modal('hide');
    $('#errorReviewModal').modal('hide');
  }

  previousPage(): void {
    if (this.counter <= 0) {
      return;
    }

    this.counter = Math.max(0, this.counter - this.amount);
    this.getReviews();
  }

  nextPage(): void {
    if (this.reviews.length < this.amount) {
      return;
    }

    this.counter += this.amount;
    this.getReviews();
  }

  getStatusKey(status: number): string {
    switch (Number(status)) {
      case this.REVIEW_STATUS_APPROVED:
        return 'REVIEW_APPROVED';
      case this.REVIEW_STATUS_REJECTED:
        return 'REVIEW_REJECTED';
      case this.REVIEW_STATUS_ARCHIVED:
        return 'REVIEW_ARCHIVED';
      default:
        return 'REVIEW_PENDING';
    }
  }

  getStatusClass(status: number): string {
    switch (Number(status)) {
      case this.REVIEW_STATUS_APPROVED:
        return 'is-approved';
      case this.REVIEW_STATUS_REJECTED:
        return 'is-rejected';
      case this.REVIEW_STATUS_ARCHIVED:
        return 'is-archived';
      default:
        return 'is-pending';
    }
  }

  getReviewerName(review: any): string {
    return review?.reviewerName || review?.clientName || review?.userName || '-';
  }

  getReviewerEmail(review: any): string {
    return review?.reviewerEmail || review?.clientEmail || review?.email || '-';
  }

  getReviewMaxRating(review: any): number {
    const maxRating = Number(review?.maxRating || 5);
    return Number.isFinite(maxRating) && maxRating > 0 ? maxRating : 5;
  }

  getProductName(review: any): string {
    return review?.productName || review?.itemName || review?.productId || '-';
  }

  hasActiveFilters(): boolean {
    return Boolean(String(this.search || '').trim() || this.status !== '' || this.componentId !== '');
  }

  getCurrentPage(): number {
    return Math.floor(this.counter / this.amount) + 1;
  }

  getTotalPages(): number {
    const total = Math.max(Number(this.totalAmount || 0), this.counter + this.reviews.length);
    return Math.max(1, Math.ceil(total / this.amount));
  }

  getSourceLabel(review: any): string {
    const source = Number(review?.reviewSource || review?.source || 0);
    if (source === 2) {
      return 'REVIEW_SOURCE_BACKOFFICE';
    }

    if (source === 3) {
      return 'REVIEW_SOURCE_IMPORTED';
    }

    if (source === 1 || review?.clientId) {
      return 'REVIEW_SOURCE_CLIENT';
    }

    return 'REVIEW_SOURCE_GUEST';
  }

  truncate(value: string, maxLength: number = 120): string {
    const normalizedValue = String(value || '').trim();
    if (normalizedValue.length <= maxLength) {
      return normalizedValue;
    }

    return `${normalizedValue.slice(0, maxLength)}...`;
  }

  private async updateReviewStatus(review: any, status: number, archive: boolean = false): Promise<void> {
    this.actionLoading = true;
    try {
      if (archive) {
        await this.lambdaService.deleteReview(Number(review.reviewId || 0), this.themingService.shopId);
      } else {
        await this.lambdaService.updateReviewStatus(Number(review.reviewId || 0), this.themingService.shopId, status);
      }

      $('#successReviewModal').modal('show');
      await this.getReviews();
    } catch (error) {
      console.error('Error updating review status:', error);
      $('#errorReviewModal').modal('show');
    } finally {
      this.actionLoading = false;
    }
  }

  private clearPendingAction(): void {
    this.pendingReview = null;
    this.pendingAction = null;
  }

  private extractReviews(response: any): any[] {
    if (Array.isArray(response?.reviews)) {
      return response.reviews;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.data?.reviews)) {
      return response.data.reviews;
    }

    if (Array.isArray(response?.[0])) {
      return response[0];
    }

    return [];
  }
}
